import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useViewport } from "@/ds";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/brand/Logo";
import GuideModal from "@/components/guides/GuideModal";
import NotificationBell from "@/components/notifications/NotificationBell";
import PanelRow from "@/components/nav/PanelRow";
import { panelGroupsFor } from "@/components/nav/panelSections";

/**
 * The Panel index — the mobile home of everything the bottom bar's five cells
 * cannot hold (SPEC_NAVEGACION_MOVIL, "Panel", mockup screen 09).
 *
 * It is a route of its own, and it has to be: the spec makes "Resumen" a row
 * inside the Panel, so the Panel cannot also BE the overview that row opens.
 * Above 992px there is a sidebar with all of this in it, so this address
 * redirects there rather than offering a second, narrower copy of it.
 *
 * The tab bar stays, with Menú active — `tabs.js` lists `/panel` among the
 * prefixes that light it.
 */
export default function Panel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isNavMobile } = useViewport();
  const [stats, setStats] = useState(null);
  const [directedPending, setDirectedPending] = useState(0);
  const [guideOpen, setGuideOpen] = useState(false);
  const [methods, setMethods] = useState([]);

  const role = user?.profile?.role;

  useEffect(() => {
    if (!role) return;
    api.get("/users/me/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [role]);

  useEffect(() => {
    if (role !== "provider") return;
    api.get("/users/me/payment-methods")
      .then((r) => setMethods(r.data.methods || []))
      .catch(() => {});
  }, [role]);

  useEffect(() => {
    if (role !== "provider") return;
    // Same request and same reason as DashboardLayout's: the count is its own
    // query on the server so a badge can ask for it without paying for a page.
    api.get("/jobs?directed=true&limit=1")
      .then((r) => setDirectedPending(r.data.pending || 0))
      .catch(() => {});
  }, [role]);

  // An admin has no Panel of this shape — eight sections, its own drawer.
  if (role === "admin" || user?.profile?.is_admin) return <Navigate to="/admin" replace />;
  if (!isNavMobile) {
    return <Navigate to={role === "provider" ? "/provider/dashboard" : "/client/dashboard"} replace />;
  }

  const verified = !!user?.profile?.is_verified;
  const groups = panelGroupsFor({ role, stats, directedPending, verified, methods });
  const firstName = String(user?.profile?.full_name || "").trim().split(/\s+/)[0] || "";

  return (
    <div className="app-shell bg-cream" data-testid="panel-page">
      {/* The same mark row DashboardLayout wears below 992px, and since T14-M1
          the same bell at the end of it. This one is NOT fixed — it scrolls
          with the Panel — which is exactly why the dropdown measures its own
          anchor instead of assuming a header height. */}
      <div className="flex items-center justify-between p-3 border-b border-sand">
        <Logo size={24} />
        <NotificationBell />
      </div>

      <div className="p-4" style={{ display: "grid", gap: 12 }}>
        <h1 className="font-display font-bold text-worker" style={{ fontSize: 30, letterSpacing: "-.02em", margin: 0 }}>
          {t("nav.dashboard")}
        </h1>
        {/* The count sits in mono inside the sentence, as the mockup sets it:
            a number you can compare at a glance with the one on the row below.
            `quotesOpen` counts quotes still in `sent` — the ones waiting on an
            answer, which is what "abiertas" means. */}
        <p className="text-smoke" style={{ margin: 0 }}>
          {t("panel.greeting", { name: firstName })}
          {role === "provider" && stats && (
            <span data-testid="panel-quote-count">
              {" "}
              <Trans
                i18nKey="panel.quotesOpen"
                count={stats.quotesOpen ?? 0}
                values={{ count: stats.quotesOpen ?? 0 }}
                components={{
                  n: <b style={{ font: "500 15px var(--font-mono)", color: "var(--worker)" }} />,
                }}
              />
            </span>
          )}
        </p>

        {groups.map((group) => (
          <React.Fragment key={group.key}>
            {group.eyebrowKey && (
              <p
                style={{
                  font: "700 11px var(--font-display)", letterSpacing: ".12em",
                  textTransform: "uppercase", color: "var(--smoke)",
                  padding: "8px 4px 0", margin: 0,
                }}
              >
                {t(group.eyebrowKey)}
              </p>
            )}
            <div
              style={{
                background: "var(--paper)", border: "1px solid var(--sand)",
                borderRadius: 16, padding: "0 12px",
              }}
              data-testid={`panel-group-${group.key}`}
            >
              {group.rows.map((row, i) => (
                <PanelRow
                  key={row.key}
                  icon={row.icon}
                  label={row.label || t(row.labelKey)}
                  mono={row.mono}
                  accent={row.accent}
                  to={row.to}
                  onClick={row.action === "guide" ? () => setGuideOpen(true) : undefined}
                  count={row.count}
                  pill={row.pill === "verified" ? t("common.verified") : null}
                  last={i === group.rows.length - 1}
                  testId={`panel-${row.key}`}
                />
              ))}
            </div>
          </React.Fragment>
        ))}
      </div>

      <GuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
